/**
 * Utility functions for property data formatting and display
 */

import theme from "../theme";

// Property constants shared across components
export const maxPropertiesPrice = 8000000;
export const maxPropertiesPerPage = 24;
// The listing search goes no deeper than this (backend schemas.py MAX_SEARCH_PAGES).
export const maxSearchPages = 30;

// Property filter options (base data without icons)
export const PropertySubTypeData = [
  { value: 'Detached', label: 'Detached' },
  { value: 'Semi-Detached', label: 'Semi-Detached' },
  { value: 'Condo Apartment', label: 'Condo Apartment' },
  { value: 'Att/Row/Townhouse', label: 'Freehold Townhouse' },
  { value: 'Condo Townhouse', label: 'Condo Townhouse' },
  { value: 'Link', label: 'Link' },
  { value: 'Duplex', label: 'Duplex' },
  { value: 'Multiplex', label: 'Multiplex' },
  { value: 'Vacant Land', label: 'Vacant Land' },
  { value: 'Farm', label: 'Farm' },
  { value: 'Others', label: 'Others' }
];

export const ListingTypeData = [
  { value: '1', label: 'For Sale' },
  { value: '0', label: 'For Rent' }
];

/**
 * Calculate days since listing was posted
 * @param {string|Date} listingDate - The listing date
 * @returns {string} Formatted string showing time since listing
 */
export const calculateDaysSinceListing = (listingDate) => {
  if (!listingDate) return 'N/A';
  
  const now = new Date();
  const listing = new Date(listingDate);
  
  // Check if it's the same date (today)
  const isToday = now.toDateString() === listing.toDateString();
  
  if (isToday) {
    const diffTime = Math.abs(now - listing);
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));
    
    if (diffHours > 0) {
      return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    } else if (diffMinutes > 0) {
      return `${diffMinutes} minute${diffMinutes > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  }
  
  // Calendar days, so the 15th read on the 30th is 15 days whatever the time of day.
  const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffDays = Math.abs(Math.round((startOfDay(now) - startOfDay(listing)) / (1000 * 60 * 60 * 24)));
  return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
};

/**
 * Where a search points, to centre "homes near you" on next time: the listing itself when
 * the search was its listing key, else the middle of the homes found (the median, so a few
 * far-off matches don't pull it away).
 * @param {string} term - The search text (a city or a listing key)
 * @param {Array} properties - The homes the search returned
 * @returns {{lat: number, lng: number, label: string}|null}
 */
export const getSearchedPlace = (term, properties) => {
  const search = String(term || '').trim();
  const located = (properties || []).filter((property) => {
    const lat = Number(property.Latitude);
    const lng = Number(property.Longitude);
    return property.Latitude != null && property.Longitude != null
      && Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);
  });
  if (!search || located.length === 0) return null;

  const same = (value) => String(value || '').toLowerCase() === search.toLowerCase();
  const listing = located.find((property) => same(property.ListingKey));
  if (listing) {
    const street = String(listing.address || listing.UnparsedAddress || '').split(',')[0].trim();
    return { lat: Number(listing.Latitude), lng: Number(listing.Longitude), label: street || listing.ListingKey };
  }

  const median = (values) => {
    const sorted = [...values].sort((a, b) => a - b);
    const middle = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
  };
  return {
    lat: median(located.map((property) => Number(property.Latitude))),
    lng: median(located.map((property) => Number(property.Longitude))),
    label: located.find((property) => same(property.City))?.City || search,
  };
};

/**
 * Calculate map center from array of properties
 * @param {Array} properties - Array of property objects with Latitude/Longitude
 * @returns {Object} Map center coordinates {lat, lng}
 */
export const calculateMapCenter = (properties) => {
  // Filter properties that have valid coordinates
  const validProperties = properties.filter(
    (property) =>
      property.Latitude !== undefined &&
      property.Latitude !== null &&
      property.Latitude !== '' &&
      property.Longitude !== undefined &&
      property.Longitude !== null &&
      property.Longitude !== '' &&
      !isNaN(property.Latitude) &&
      !isNaN(property.Longitude)
  );

  if (validProperties.length === 0) {
    // Default to Toronto if no valid coordinates
    return { lat: 43.6532, lng: -79.3832 };
  }

  // Calculate the center point
  const totalLat = validProperties.reduce((sum, property) => sum + parseFloat(property.Latitude), 0);
  const totalLng = validProperties.reduce((sum, property) => sum + parseFloat(property.Longitude), 0);

  return {
    lat: totalLat / validProperties.length,
    lng: totalLng / validProperties.length
  };
};

/**
 * Calculate appropriate zoom level based on property spread
 * @param {Array} properties - Array of property objects with Latitude/Longitude
 * @returns {number} Appropriate zoom level for the map
 */
export const calculateMapZoom = (properties) => {
  const validProperties = properties.filter(
    (property) =>
      property.Latitude !== undefined &&
      property.Latitude !== null &&
      property.Latitude !== '' &&
      property.Longitude !== undefined &&
      property.Longitude !== null &&
      property.Longitude !== '' &&
      !isNaN(property.Latitude) &&
      !isNaN(property.Longitude)
  );

  if (validProperties.length === 0) return 13;
  if (validProperties.length === 1) return 15;

  // Calculate bounds
  const lats = validProperties.map(p => parseFloat(p.Latitude));
  const lngs = validProperties.map(p => parseFloat(p.Longitude));
  
  const latRange = Math.max(...lats) - Math.min(...lats);
  const lngRange = Math.max(...lngs) - Math.min(...lngs);
  const maxRange = Math.max(latRange, lngRange);

  // Determine zoom based on coordinate spread
  if (maxRange > 10) return 13;     // Very wide spread - faster loading
  if (maxRange > 5) return 13;      // Wide spread - faster loading  
  if (maxRange > 2) return 13;      // Large area - faster loading
  if (maxRange > 1) return 13;      // Medium-large area - faster loading
  if (maxRange > 0.5) return 13;    // Medium area - faster loading
  if (maxRange > 0.2) return 13;    // Small-medium area
  if (maxRange > 0.1) return 13;    // Small area
  if (maxRange > 0.05) return 13;   // Very small area
  return 14;                        // Tiny area
};


/**
 * Formats the living area display for a property
 * @param {Object} property - The property object containing living area data
 * @param {boolean} includeUnits - Whether to include units (sq ft) in the output
 * @returns {string} Formatted living area string
 */
export const formatLivingArea = (property, includeUnits = false) => {
  if (!property) return 'N/A';

  let areaText = 'N/A';
  if (property.LivingAreaRange) {
    areaText = property.LivingAreaRange;
  } else if (property.LivingArea) {
    areaText = Number(property.LivingArea).toLocaleString();
  } else if (property.LivingAreaMinimum && property.LivingAreaMaximum) {
    if (property.LivingAreaMaximum == 100000.00) {
      areaText = `${Number(property.LivingAreaMinimum).toString()}+`;
    }
    else {
      areaText = `${Number(property.LivingAreaMinimum).toString()} - ${Number(property.LivingAreaMaximum).toString()}`;
    }
  }

  if (includeUnits && areaText !== 'N/A') {
    const units = property.AboveGradeFinishedAreaUnits || property.BuildingAreaUnits || 'sq ft';
    return `${areaText} ${units}`;
  }

  return areaText;
};



/**
 * Formats any area value with proper number formatting
 * @param {number|string} area - The area value to format
 * @param {string} units - The units to append (optional)
 * @returns {string} Formatted area string
 */
export const formatArea = (area, units = '') => {
  if (!area || area === 0) return 'N/A';
  
  const formattedArea = Number(area).toLocaleString();
  return units ? `${formattedArea} ${units}` : formattedArea;
};

/**
 * Normalizes text-based multi-value columns (e.g. Cooling/Heating) into a clean list.
 * Accepts arrays, single strings, comma-separated strings, and JSON array strings.
 * @param {any} value - Raw column value from API
 * @returns {string[]} Distinct display values in original order
 */
export const formatColumnStringValues = (value) => {
  const parseTokens = (input) => {
    if (input === null || input === undefined) {
      return [];
    }

    if (Array.isArray(input)) {
      return input.flatMap((item) => parseTokens(item));
    }

    const text = String(input).trim();
    if (!text) {
      return [];
    }

    if (text.startsWith('[') && text.endsWith(']')) {
      try {
        const parsed = JSON.parse(text);
        if (Array.isArray(parsed)) {
          return parseTokens(parsed);
        }
      } catch (error) {
        // Fall through and parse the value as plain text.
      }
    }

    return text
      .split(/[;,|]/)
      .map((part) => part.trim())
      .filter(Boolean);
  };

  const seen = new Set();
  return parseTokens(value).filter((item) => {
    const key = item.toLowerCase();
    if (seen.has(key)) {
      return false;
    }
    seen.add(key);
    return true;
  });
};

/**
 * True when a listing is for lease. ListingType is the feed's sale flag:
 * 1/true for sale, 0/false for lease. Same rule as _is_lease() in
 * backend/app_fastapi/services/seo_render_service.py.
 * @param {Object} property
 * @returns {boolean}
 */
export const isRentListing = (property) => {
  const listingType = property?.ListingType;
  if (typeof listingType === 'string') {
    const normalized = listingType.trim().toLowerCase();
    return normalized === '0' || normalized === 'false' || normalized === 'rent' || normalized === 'for rent';
  }
  return listingType === 0 || listingType === false;
};

/**
 * Gets the property price based on listing type (sale vs rent)
 * @param {Object} property - The property object
 * @param {boolean} formatted - Whether to return formatted price (with M, K suffix)
 * @returns {string} Formatted price
 */
export const getPropertyPrice = (property, formatted = false) => {
  if (!property) return '0';

  const listingType = property.ListingType;
  const isRent = isRentListing(property);

  if (listingType === null || listingType === undefined) {
    return 'N/A';
  }

  const price = isRent
    ? (property.LeaseAmount ?? property.ListPrice)
    : (property.ListPrice ?? property.LeaseAmount);

  if (formatted) {
    return formatPrice(price);
  } else {
    return Number(price || 0); //.toLocaleString();
  }
};

/**
 * Formats price values for display.
 * Sale values use compact K/M formatting; rent values keep finer precision.
 * @param {number|string} price - The price to format
 * @param {Object} options - Formatting options
 * @param {boolean} options.isRent - Whether the value is rent pricing
 * @returns {string} Formatted price string
 */
export const formatPrice = (price) => {
  if (!price) return 'N/A';

  const numPrice = Number(price);

  if (numPrice >= 1_000_000) {
    return `${(numPrice / 1_000_000).toFixed(1)}M`.replace('.0', '');
  }
  if (numPrice >= 100_000) {
    return `${(numPrice / 1_000).toFixed(0)}K`.replace('.0', '');
  }
  if (numPrice >= 10_000) {
    return `${(numPrice / 1_000).toFixed(1)}K`.replace('.0', '');
  }
  if (numPrice >= 1_000) {
    return `${(numPrice / 1_000).toFixed(1)}K`.replace('.0', '');
  }
  return `${Math.round(numPrice)}`;
};

/**
 * Formats a price as Canadian currency for display in cards, detail pages and
 * tables: $850,000 (design.md §5.8). Use `formatPrice` instead only where space
 * is genuinely tight, such as map markers, which need the compact "850K" form.
 * @param {number|string} price
 * @param {Object} [options]
 * @param {string} [options.fallback='N/A'] shown when there is no usable price
 * @returns {string}
 */
export const formatPriceCAD = (price, { fallback = 'N/A' } = {}) => {
  const numPrice = Number(price);
  if (!price || Number.isNaN(numPrice)) return fallback;

  return numPrice.toLocaleString('en-CA', {
    style: 'currency',
    currency: 'CAD',
    maximumFractionDigits: 0,
  });
};

/**
 * A listing's price for cards, headers and the detail page, split so the
 * period can be set smaller (components/property/ListingPrice.js):
 * { amount: '$850,000', period: '' } for a sale, { amount: '$2,800',
 * period: '/month' } for a lease (a lease's price is the monthly rent).
 * @param {Object} property
 * @param {Object} [options]
 * @param {string} [options.fallback='N/A'] shown when there is no usable price
 * @returns {{amount: string, period: string}}
 */
export const listingPriceParts = (property, { fallback = 'N/A' } = {}) => {
  const price = Number(getPropertyPrice(property));
  if (!price) return { amount: fallback, period: '' };
  return { amount: formatPriceCAD(price), period: isRentListing(property) ? '/month' : '' };
};

// The unit goes first: the feed writes "95 Oneida Crescent 1109, Richmond Hill, ON
// L4B 0H5", where the unit reads as part of the street; KeyTa shows "1109 -
// 95 Oneida Crescent, Richmond Hill, ON L4B 0H5". Same rules as the backend's
// utils/listing_address.py, which builds the server-rendered h1 and title of a
// listing page; propertyUtils.test.js and test_listing_address.py share their cases.

// A unit already named by a word is shown as it is ("Upper", "Locker P2 #22",
// "Suite 200"); a number or code becomes "1109", "PH01", "3516A".
const NAMED_UNIT = /^[A-Za-z]{3,}(?:\s|$)/;
// How a unit may already be marked in the address.
const UNIT_MARK = '(?:(?:unit|apt|apartment|suite|ste)\\.?\\s*|#)?';

const cleanUnit = (unit) => String(unit || '').trim().replace(/^,+|,+$/g, '').trim();
const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/** "1109" for a number or code; a unit named by a word as it is. */
export const unitLabel = (unit) => {
  const text = cleanUnit(unit).replace(/^#+/, '').trim();
  if (!text) return '';
  if (NAMED_UNIT.test(text)) return text[0].toUpperCase() + text.slice(1);
  return `${text}`;
};

/**
 * The address with its unit moved to the front. Unchanged without a unit, and
 * unchanged when the unit is already in front, so it can be applied twice.
 * @param {string} address
 * @param {string|number} UnitNumber
 * @returns {string}
 */
export const unitFirstAddress = (address, unit) => {
  const text = String(address || '').trim();
  const label = unitLabel(unit);
  if (!label) return text;
  if (!text || text.toLowerCase() === label.toLowerCase()) return label;

  const bare = escapeRegExp(cleanUnit(unit).replace(/^#+/, '').trim());
  let street;
  let rest;
  // The feed's form: the unit ends the street, just before the first comma.
  const match = new RegExp(`\\s${UNIT_MARK}${bare},`, 'i').exec(text);
  if (match) {
    street = text.slice(0, match.index);
    rest = text.slice(match.index + match[0].length);
  } else {
    const comma = text.indexOf(',');
    street = comma === -1 ? text : text.slice(0, comma);
    rest = comma === -1 ? '' : text.slice(comma + 1);
    // Already in front ("1109-95 Oneida Crescent", "1109 - 95 ..."), or ending a
    // street that has no locality after it.
    street = street.trim()
      .replace(new RegExp(`^${UNIT_MARK}${bare}\\s*[-–]\\s*`, 'i'), '')
      .replace(new RegExp(`\\s${UNIT_MARK}${bare}$`, 'i'), '');
  }
  const trimEdges = (value) => value.replace(/^[ ,]+|[ ,]+$/g, '');
  street = trimEdges(street);
  rest = trimEdges(rest);
  return `${label} - ${street}${rest ? `, ${rest}` : ''}`;
};

/**
 * The one address resolver. RESO/VOW records, user-created properties and the
 * admin API each name the field differently, so every card and detail view
 * reads the address through this instead of picking its own precedence.
 * A condo's unit comes first (unitFirstAddress).
 * @param {Object} property
 * @param {Object} [options]
 * @param {string} [options.fallback='Address unavailable']
 * @returns {string}
 */
export const formatAddress = (property, { fallback = 'Address unavailable' } = {}) => {
  if (!property) return fallback;

  const direct =
    property.UnparsedAddress ||
    property.full_address ||
    property.address ||
    property.Address;
  if (direct) return unitFirstAddress(direct, property.UnitNumber);

  const streetOnly = [property.StreetNumber, property.StreetName, property.StreetSuffix]
    .filter(Boolean)
    .join(' ')
    .trim();
  const street = streetOnly && unitFirstAddress(streetOnly, property.UnitNumber);
  const city = property.City || property.city;
  const composed = [street, city].filter(Boolean).join(', ');

  return composed || fallback;
};

const toCount = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(0, Math.trunc(number)) : null;
};

/**
 * Parking as a count: the covered spaces, then the rest ("2 + 4" is a two-car garage
 * and four outside), or the total alone when none or all are covered. The label says
 * it in words, for a tooltip. Search cards and the KeyTa listing page's stat show
 * it; detail views print the backend's ParkingDetails ("2 Attached Garage + 4 Driveway").
 * @param {Object} property - ParkingTotal and CoveredSpaces
 * @returns {{ text: string, label: string } | null} null when the listing gives no count
 */
export const parkingCount = (property) => {
  const total = toCount(property?.ParkingTotal) ?? toCount(property?.CoveredSpaces);
  if (total === null) return null;
  if (total === 0) return { text: '0', label: 'No parking' };

  // A garage count above the total is a feed error; the total wins.
  const covered = Math.min(toCount(property?.CoveredSpaces) ?? 0, total);
  const outside = total - covered;
  const spaces = (count) => `parking space${count === 1 ? '' : 's'}`;
  if (covered && outside) {
    return { text: `${covered} + ${outside}`, label: `${covered} covered + ${outside} uncovered ${spaces(total)}` };
  }
  return { text: String(total), label: `${total} ${covered ? 'covered ' : ''}${spaces(total)}` };
};

/**
 * Condominium ownership, the seller form's rule (backend constants.property
 * is_condo_ownership): PropertyType 'Residential Condo & Other'; without a type, the subtype.
 */
export const isCondoOwnership = (propertyType, propertySubType) => {
  if (propertyType) return String(propertyType).trim().toLowerCase() === 'residential condo & other';
  const subType = String(propertySubType || '').toLowerCase();
  return ['condo', 'co-op', 'co-ownership'].some((word) => subType.includes(word));
};

// (field, label), in the order the pages print them.
const CONDO_UNIT_FACTS = [['Exposure', 'Exposure'], ['BalconyType', 'Balcony'], ['Locker', 'Locker']];

// Condos whose units are homes on the ground (backend utils/condo_facts.py
// GROUND_CONDO_SUBTYPES): a basement is expected, a locker or a balcony is not.
const GROUND_CONDO_SUBTYPES = new Set(['condo townhouse', 'detached condo', 'semi-detached condo', 'vacant land condo']);
const isGroundCondo = (property) => GROUND_CONDO_SUBTYPES.has(String(property?.PropertySubType || '').trim().toLowerCase());

/**
 * A condo unit's own facts: [[label, value]] for Exposure, Balcony and Locker; [] for
 * any other home. Same rules as the backend's utils/condo_facts.py, because a listing
 * page prints them server-rendered and in React alike (condoUnitFacts.cases.json).
 * "Unknown", or an exposure of "None", says nothing. An apartment's "None" balcony or
 * locker is an answer; a townhouse's is no news, since it seldom has either.
 * @param {Object} property
 * @returns {Array<[string, string]>}
 */
export const condoUnitFacts = (property) => {
  if (!property || !isCondoOwnership(property.PropertyType, property.PropertySubType)) return [];
  const ground = isGroundCondo(property);
  const text = (value) => {
    const joined = Array.isArray(value)
      ? value.map((item) => String(item ?? '').trim()).filter(Boolean).join(', ')
      : String(value ?? '').trim();
    // The feed joins two answers with a bare "+" ("Ensuite+Owned").
    return joined.replace(/\s*\+\s*/g, ' + ');
  };
  return CONDO_UNIT_FACTS
    .map(([field, label]) => [field, label, text(property[field])])
    .filter(([field, , value]) => value
      && value.toLowerCase() !== 'unknown'
      && !(value.toLowerCase() === 'none' && (field === 'Exposure' || ground)))
    .map(([, label, value]) => [label, value]);
};

// Values that name no basement: what condo apartments carry ("None", and filler such as
// "Other" or "Apartment").
const NO_BASEMENT = new Set(['none', 'other', 'apartment']);

/**
 * The basement as listing pages show it, or '' to leave it out. Same rules as the
 * backend's utils/condo_facts.py basement_text: a house, or a condo on the ground (a
 * townhouse...), shows what the listing says, "None" included; a condo apartment only a
 * basement it names ("Finished"), since they carry "None".
 * @param {Object} property
 * @returns {string}
 */
export const basementText = (property) => {
  const raw = property?.Basement;
  const text = Array.isArray(raw)
    ? raw.map((item) => String(item ?? '').trim()).filter(Boolean).join(', ')
    : String(raw ?? '').trim();
  if (!text || !isCondoOwnership(property.PropertyType, property.PropertySubType) || isGroundCondo(property)) {
    return text;
  }
  const named = text.split(',').some((part) => !NO_BASEMENT.has(part.trim().toLowerCase()));
  return named ? text : '';
};

/**
 * Gets property type abbreviation for display on markers
 * @param {string} structureType - The property structure type
 * @returns {string} Abbreviated property type
 */
export const getPropertyTypeAbbreviation = (structureType) => {
  if (!structureType) return 'R';
  
  const type = structureType.toLowerCase();
  if (type.includes('house') || type.includes('detached')) return 'D';
  if (type.includes('condo') || type.includes('strata') || type.includes('apartment')) return 'C';
  if (type.includes('townhouse') || type.includes('townhome') || type.includes('row')) return 'TH';
  if (type.includes('duplex')) return 'DX';
  if (type.includes('triplex')) return 'TX';
  if (type.includes('fourplex')) return 'FX';
  if (type.includes('multi family')) return 'MF';
  if (type.includes('vacant land')) return 'L';
  if (type.includes('agriculture')) return 'AG';
  if (type.includes('recreational')) return 'REC';
  if (type.includes('parking')) return 'P';
  return 'R'; // Default to 'R' for Residential
};

/**
 * Creates a single property marker icon for Google Maps
 * @param {Object} property - The property object
 * @param {boolean} isSelected - Whether the marker is selected
 * @returns {Object} Google Maps marker icon configuration
 */
export const createPropertyMarkerIcon = (property, isSelected = false) => {
  const typeAbbreviation = getPropertyTypeAbbreviation(property.StructureType);
  const formattedPrice = getPropertyPrice(property, true);
  const displayText = `${typeAbbreviation} ${formattedPrice}`;
  
  const svg = `
    <svg width="66" height="35" viewBox="0 0 80 35" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000000" flood-opacity="0.25"/>
        </filter>
      </defs>
      <!-- Main marker background -->
      <rect x="8" y="4" width="60" height="22" rx="12" ry="12" 
            fill="${isSelected ? '#2A5D68' : '#3B7B86'}" 
            stroke="white" 
            stroke-width="1" 
            filter="url(#shadow)"/>
      <!-- Pointer triangle -->
      <polygon points="38,33 33,27 43,27" 
               fill="${isSelected ? '#2A5D68' : '#3B7B86'}" 
               stroke="white" 
               stroke-width="1"/>
      <!-- Text -->
      <text x="37" y="19" 
            font-family="'Inter', 'Roboto', sans-serif" 
            font-size="10.5" 
            font-weight="600" 
            fill="white" 
            text-anchor="middle">
        ${displayText}
      </text>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(80, 35),
    anchor: new window.google.maps.Point(40, 35),
  };
};

/**
 * Creates a cluster marker icon for Google Maps
 * @param {number} count - Number of properties in the cluster
 * @param {boolean} isSelected - Whether the marker is selected
 * @returns {Object} Google Maps marker icon configuration
 */
export const createClusterMarkerIcon = (count, isSelected = false) => {
  const size = count <= 10 ? 32 : count <= 50 ? 40 : 48;
  const color = '#048134'; // Consistent green color for all cluster sizes
  
  const svg = `
    <svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="3" flood-color="#000000" flood-opacity="0.3"/>
        </filter>
      </defs>
      <!-- Outer circle -->
      <circle cx="${size/2}" cy="${size/2}" r="${size/2-2}" 
              fill="${isSelected ? '#2A5D68' : color}" 
              stroke="white" 
              stroke-width="0" 
              filter="url(#shadow)"/>
      <!-- Inner circle -->
      <circle cx="${size/2}" cy="${size/2}" r="${size/2-3}" 
              fill="rgba(255,255,255,0.3)" 
              stroke="none"/>
      <!-- Text -->
      <text x="${size/2}" y="${size/2+2}" 
            font-family="'Inter', 'Roboto', sans-serif" 
            font-size="${size <= 32 ? '10' : size <= 40 ? '12' : '14'}" 
            font-weight="700" 
            fill="white" 
            text-anchor="middle">
        ${count}
      </text>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size/2, size/2),
  };
};

/**
 * Creates a single property marker icon for property groups (simplified version)
 * @param {number|string} price - The property price
 * @param {boolean} isSelected - Whether the marker is selected
 * @param {boolean} isRent - Whether this marker price is rent
 * @returns {Object} Google Maps marker icon configuration
 */
export const createSinglePropertyGroupMarkerIcon = (price, isSelected = false, isRent = false) => {
  const formattedPrice = price ? formatPrice(price, { isRent }) : 'N/A';
  const displayText = `${formattedPrice}`;
  
  const svg = `
    <svg width="66" height="35" viewBox="0 0 80 35" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#000000" flood-opacity="0.25"/>
        </filter>
      </defs>
      <!-- Main marker background -->
      <rect x="8" y="4" width="60" height="22" rx="12" ry="12" 
            fill="${isSelected ? '#2A5D68' : '#3B7B86'}" 
            stroke="white" 
            stroke-width="1" 
            filter="url(#shadow)"/>
      <!-- Pointer triangle -->
      <polygon points="38,33 33,27 43,27" 
               fill="${isSelected ? '#2A5D68' : '#3B7B86'}" 
               stroke="white" 
               stroke-width="1"/>
      <!-- Text -->
      <text x="37" y="19" 
            font-family="'Inter', 'Roboto', sans-serif" 
            font-size="10.5" 
            font-weight="600" 
            fill="white" 
            text-anchor="middle">
        ${displayText}
      </text>
    </svg>
  `;

  return {
    url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`,
    scaledSize: new window.google.maps.Size(80, 35),
    anchor: new window.google.maps.Point(40, 35),
  };
};